import { lazy, Suspense } from 'react';
import { isInsideFlutterApp } from './utils/storeLinks';
import { isHidabrut, prepareHidabrutProfileStorage } from './utils/hidabrutPayload';
import SpinnerOverlay from './components/SpinnerOverlay';

// The full product — Firebase, i18n, Mixpanel, deep-link/affiliate tracking,
// FCM sync, every onboarding/practice/portal page — lazy so a marketing
// visitor never downloads any of it.
const AppShell = lazy(() => import('./AppShell'));
// The public "/" marketing page, in its own lazy chunk so it never pulls in
// AppShell's imports (or vice versa) through a shared static import.
const LandingPage = lazy(() => import('./pages/LandingPage'));

// Decide, before either bundle loads, whether "/" should be the marketing
// landing page or the real app.
//
// For the "/" decision itself, `app` in localStorage is the ONLY thing that
// decides — no "returning user" heuristic (hasPassedOnboarding + loggedInUser)
// anymore: that fallback is exactly what made this behave unpredictably
// (a browser with leftover login state from earlier testing would show the
// app even with the flag unset/false). Set localStorage.setItem('app', 'true')
// to get the app shell at "/"; anything else (unset, 'false', ...) always
// gets the landing page.
//
// Three checks stay ahead of that flag, since they're real product behavior,
// not test toggles:
// - isInsideFlutterApp() / isHidabrut(): the real mobile app's WebView (or the
//   Hidabrut app's) must always get the app shell, independent of this flag.
// - any path other than "/": every other route (/practice, /try, /get-app, ...)
//   only exists inside AppShell, so it's the only thing that can render them.
// - a shared connection link ("/?connectionId=..." from WhatsApp/QR) must
//   reach OnboardingPage, the only place that redeems it, or the link does
//   nothing for whoever clicked it.
function shouldShowAppShell(): boolean {
  if (typeof window === 'undefined') return true;
  if (isInsideFlutterApp()) return true;
  if (isHidabrut()) return true;
  if (window.location.pathname !== '/') return true;
  if (new URLSearchParams(window.location.search).get('connectionId')) return true;
  return localStorage.getItem('app') === 'true';
}

// Hidabrut profiles share this WebView's localStorage: swap in the opened profile's
// data before anything reads it.
prepareHidabrutProfileStorage();

function App() {
  return (
    <Suspense fallback={<SpinnerOverlay message="" />}>
      {shouldShowAppShell() ? <AppShell /> : <LandingPage />}
    </Suspense>
  );
}

export default App;
